<template>
  <el-tabs
    v-if="allTabs.length > 1"
    class="mb-3 tabs-container"
    @tab-click="tabClick"
    :activeName="activeTab"
    :type="properties.type"
    :stretch="properties.stretch"
    :tabPosition="properties.tabPosition"
    :class="{
      'type-card': properties.type == 'card',
      'type-border-card': properties.type == 'border-card',
    }"
  >
    <el-tab-pane
      v-for="(tab, tabId) in shownTabs"
      v-if="tab.rows.length"
      :key="tabId"
      :label="$i18n.t(getTabTitle(position, tab.name))"
      :lazy="true"
      :name="tab.name"
      class="tab-default-height"
    >
      <div class="row" v-for="(row, idx) in tab.rows" :key="idx">
        <div v-for="item in row.items" class="mb-4" :class="item.position.classes" :key="item.position.i">
          <DotoComponent
            :url="url"
            :params="params"
            :component="getItemType(item)"
            :uid="`${gridId}-${item.position.i}`"
            :class="getPanelClass(item)"
          />
        </div>
      </div>
    </el-tab-pane>

    <el-tab-pane v-if="areMoreTabsActive" disabled name="more-tabs-dropdown" :name="activeInMore.tabId">
      <el-dropdown
        split-button
        slot="label"
        trigger="click"
        class="w-100 tabs-dropdown"
        :class="{ 'is-active': activeInMore.isActive }"
        type="default"
        @command="handleSelect"
        @click="handleClick"
      >
        <span class="d-block" :class="{ 'is-active__color': activeInMore.isActive }">
          {{ $i18n.t(dropdownLabel) }}
        </span>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item v-for="tabId in hiddenTabNames" :key="tabId" :command="tabId">
            {{ $i18n.t(getTabTitle(position, tabId)) }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>

      <template v-if="activeInMore.tab">
        <div class="row" v-for="(panelRow, idx) in activeInMore.tab.rows" :key="idx">
          <div v-for="item in panelRow.items" class="mb-4" :class="item.position.classes" :key="item.position.i">
            <DotoComponent
              :url="url"
              :params="params"
              :component="getItemType(item)"
              :uid="`${gridId}-${item.position.i}`"
              :class="getPanelClass(item)"
            />
          </div>
        </div>
      </template>
    </el-tab-pane>
  </el-tabs>

  <div v-else>
    <div v-for="(tab, idx) in allTabs" :key="idx">
      <div class="row" v-for="(panelRow, idx) in tab.rows" :key="idx">
        <div v-for="item in panelRow.items" class="mb-4" :class="item.position.classes" :key="item.position.i">
          <DotoComponent
            :url="url"
            :component="getItemType(item)"
            :uid="`${gridId}-${item.position.i}`"
            :params="params"
            :class="getPanelClass(item)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DotoComponent from './DotoComponent'
import { get, difference, has } from 'lodash'
import { UNASSIGNED_PANELS_TAB } from '../lib/helpers'
import { recursiveResolveCoreParam } from '../../../utils'

export default {
  name: 'Position',
  components: {
    DotoComponent,
  },
  props: {
    grid: {
      type: Object,
      required: true,
    },
    position: {
      type: String,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    params: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeTab: '',
      shownTabs: [],
      hiddenTabs: [],
      hiddenTabNames: [],
      activeInMore: {
        tabId: '',
        isActive: false,
        tab: null,
      },
    }
  },
  computed: {
    allTabs() {
      // create the container for tabs
      let layoutTabs = {}
      // filter panels assigned to current position
      const filteredPanels = this.gridItems.filter((item) => item.columnId === this.position)
      // tabs are saved in desired order, so we assign the properties to the container in this order
      const tabsInThisPosition = get(this.grid, ['tabs', this.position], [])
      tabsInThisPosition.forEach((tab) => (layoutTabs[tab.name] = []))
      // assign panels to their respective tabs
      filteredPanels.map((panel) => {
        if (!layoutTabs[panel.tabId]) layoutTabs[panel.tabId] = []
        layoutTabs[panel.tabId].push(panel)
      })
      const tabEntries = Object.entries(layoutTabs)
      let result = []
      // for each tab
      for (const [tabName, tab] of tabEntries) {
        // separate rows inside tabs based on panel y positions
        let rows = tab.reduce((acc, item) => {
          let row = acc.find((element) => element.row === item.position.y)
          if (row) {
            row.items.push(item)
          } else {
            acc.push({
              row: item.position.y,
              items: [item],
            })
          }
          return acc
        }, [])
        // order each panel inside their respective row based on their x position
        rows = rows.map((row) => {
          row.items.sort((a, b) => a.position.x - b.position.x)
          return row
        })
        // order the rows themselves in case the first panel wasn't in the first row
        rows.sort((a, b) => a.row - b.row)
        // assign classes based on panel sizes
        rows = rows.map((row) => {
          row.items = row.items.map((item, i, items) => {
            item.position.classes = [`col-md-${item.position.w}`]
            let prevRTC = i === 0 ? 0 : items[i - 1].position.x + items[i - 1].position.w
            let posDist = item.position.x - prevRTC
            if (posDist >= 1) {
              item.position.classes.push(`offset-md-${posDist}`)
            }
            return item
          })
          return row
        })
        // a tabEntry is done
        result.push({ name: tabName, rows })
      }
      // tabs ready for el-tab
      return result
    },
    gridId() {
      return get(this.grid, 'id', '')
    },
    gridItems() {
      return get(this.grid, 'gridItems', [])
    },
    properties() {
      return get(this.grid, 'tabProperties', {
        type: '',
        stretch: false,
        tabPosition: 'top',
        numberOfShownTabs: 5,
      })
    },
    tabs() {
      return get(this.grid, 'tabs', {})
    },
    areMoreTabsActive() {
      return this.properties.numberOfShownTabs < this.allTabs.length
    },
    dropdownLabel() {
      return get(this.activeInMore, 'tabId', null)
        ? this.getTabTitle(this.position, this.activeInMore.tabId)
        : 'more_tabs'
    },
  },
  methods: {
    getItemType: function (item) {
      if (!item) return
      if (item.type === 'CustomComponent') {
        item.type = item.params.base.componentName
        if (item.params.base.componentProps) {
          const componentProps = JSON.parse(item.params.base.componentProps)
          const componentKeys = Object.keys(componentProps)
          const componentValues = Object.values(componentProps)
          let obj = {}
          for (let index in componentKeys) {
            obj[componentKeys[index]] = recursiveResolveCoreParam({input: componentValues[index], core: this.$CORE})
          }
          Object.assign(item.params.base, obj)
          delete item.params.base.componentName
          delete item.params.base.componentProps
        }
      }
      return item
    },
    getPanelClass(item) {
      return get(item, ['panel', 'panelClass'], '')
    },
    getTabTitle(columnId, tabId) {
      const position = get(this.tabs, columnId, [])
      const tab = position.find((tab) => tab.name === tabId)
      return tab ? tab.title : UNASSIGNED_PANELS_TAB.title
    },
    changeTabInParams(name) {
      if (this.position) {
        const key = `${this.position}Tab`
        if (has(window.CORE.params, key)) {
          window.CORE.params[key] = name
        }
      }
    },
    tabClick({ _props }) {
      this.activeInMore.isActive = false
      this.activeTab = _props.name
      this.insertUrlParam(_props.name)
      this.changeTabInParams(_props.name)
      this.toggleActiveBackground()
    },
    insertUrlParam(value) {
      if (history.pushState) {
        const key = `${this.position}Tab`
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.set(key, value)
        const newUrl = `${window.location.protocol}//${window.location.host}${
          window.location.pathname
        }?${searchParams.toString()}`
        window.history.pushState({ path: newUrl }, '', newUrl)
      }
    },
    deleteUrlParam(key) {
      if (history.pushState) {
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.delete(key)
        const newUrl = `${window.location.protocol}//${window.location.host}${
          window.location.pathname
        }?${searchParams.toString()}`
        window.history.pushState({ path: newUrl }, '', newUrl)
      }
    },
    handleSelect(tabName) {
      if (!tabName) return

      this.activeTab = tabName
      this.activeInMore.tabId = tabName
      this.activeInMore.isActive = true
      this.activeInMore.tab = this.allTabs.find((tab) => tab.name === tabName)

      this.insertUrlParam(this.activeTab)
      this.toggleActiveBackground()
    },
    handleClick() {
      if (!this.activeInMore.tabId) return

      this.activeInMore.isActive = true
      this.activeTab = this.activeInMore.tabId

      this.insertUrlParam(this.activeTab)
      this.toggleActiveBackground()
    },
    toggleActiveBackground() {
      this.$nextTick(() => {
        const element = document.querySelector('.tabs-dropdown button')
        if (!element || !this.activeInMore) return
        this.activeInMore.isActive
          ? element.classList.add('is-active__background')
          : element.classList.remove('is-active__background')
      })
    },
    initShownTabs() {
      const limit =
        this.properties.numberOfShownTabs < this.allTabs.length
          ? this.properties.numberOfShownTabs
          : this.allTabs.length

      for (let i = 0; i < limit; i++) {
        this.shownTabs.push(this.allTabs[i])
      }
    },
    initHiddenTabs() {
      this.hiddenTabs = difference(this.allTabs, this.shownTabs)
      this.hiddenTabNames = this.hiddenTabs.map((tab) => tab.name)
    },
    setActiveTab(currentTab) {
      const tabs = this.allTabs.map((tab) => tab.name)

      if (currentTab && tabs.includes(currentTab)) {
        this.activeTab = currentTab
        if (this.hiddenTabNames.includes(currentTab)) {
          this.activeInMore.tabId = currentTab
          this.activeInMore.isActive = true
          this.activeInMore.tab = this.allTabs.find((tab) => tab.name === currentTab)
          this.toggleActiveBackground()
        }
      } else if (currentTab) {
        this.deleteUrlParam(`${this.position}Tab`)
        this.activeTab = tabs[0]
      } else {
        this.activeTab = tabs[0]
      }
    },
  },
  mounted() {
    const searchParams = new URLSearchParams(window.location.search)
    const currentTab = searchParams.get(`${this.position}Tab`)

    this.initShownTabs()
    if (this.areMoreTabsActive) this.initHiddenTabs()
    this.setActiveTab(currentTab)
  },
}
</script>

<style lang="scss">
.type-card {
  .el-tabs__item.is-active {
    color: white;
    background-color: $color-primary;
    //opacity: 0.7;
  }
  .el-tabs__item.is-active:hover {
    color: white;
  }
  .is-active__color {
    color: white !important;
  }
  .is-active__background {
    background-color: $color-primary !important;
  }
}
.type-border-card {
  .el-tabs__nav {
    border-left: 1px solid $border-color-light;
    border-right: 1px solid $border-color-light;
  }
  .el-tabs__item.is-active:first-child {
    border-left-color: $white !important;
  }
  .el-tabs__item.is-active:last-child {
    border-right-color: $white !important;
  }
  .el-tabs__item.is-active {
    color: white;
    background-color: $color-primary;
    //opacity: 0.7;
  }
  .el-tabs__item.is-active:hover {
    color: white;
  }
  .el-dropdown {
    .el-button-group {
      button:nth-child(1) {
        background-color: $default-panel-header-bg;
        span:not(.is-active__color) {
          color: $light4;
          font-weight: 500;
        }
      }
      .is-active__background {
        background-color: $white !important;
      }
      button:nth-child(2) {
        background-color: $default-panel-header-bg;
      }
    }
  }
}
.tabs-container {
  .el-tabs__item.is-disabled {
    padding: 0 !important;
    background-color: unset !important;
  }
  .el-dropdown {
    .el-button-group {
      button:nth-child(1) {
        background-color: transparent;
        border-radius: 0;
        border: unset;
        width: 80%;
        &:not(.is-disabled):active,
        &:not(.is-disabled):focus,
        &:not(.is-disabled):hover {
          z-index: unset !important;
        }
        span {
          color: $color-primary;
          font-weight: 500;
        }
      }
      button:nth-child(2) {
        background-color: transparent;
        border-right: unset;
        border-bottom: unset;
        border-top: unset;
        width: 20%;
      }
    }
  }
}
.tab-default-height {
  min-height: 520px;
}
</style>
